import { useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import selectors from "selectors";
import { useDispatch, useSelector } from "react-redux";
import { fetchPatientProfile, updatePatientProfile } from "actions/user";
import { Page } from "components/ui";
import { Tabs, Tab, useMediaQuery } from "@mui/material";
import NotificationManager from "utils/notifications";
import { omit, path } from "ramda";
import useTranslate from "hooks/use-translate-language";
import useDocumentTitle from "hooks/use-document-title";
import getInitialValues from "components/edit-profile/initialValues";
import EditProfileFormValidation from "components/edit-profile/EditProfileFormValidation";
import { createValidationErrorHandler } from "./validation-utils";
import { renderTabContent } from "./tab-content";
import { ButtonActions } from "./profile-buttons";
import { useKeyboardNavigation } from "./use-keyboard-navigation";
import "./profile.scss";
import BreadCrumbs from "components/ui_new/breadcrumbs";

const MyProfile = () => {
  const profile = useSelector(selectors.getPatientProfile);
  const translate = useTranslate();
  const [editMode, setEditMode] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width: 800px)");
  const [currentGroup, setCurrentGroup] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const dispatch = useDispatch();

  useDocumentTitle(translate("profile"));

  const saveButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const editButtonRef = useRef(null);

  const tabLabels = [
    translate("personalInfo"),
    translate("insuranceAndPhysician"),
    translate("security")
  ];
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedLabel, setSelectedLabel] = useState(tabLabels[0]);
  const editProfileFormValidation = EditProfileFormValidation(translate);

  const { showValidationErrors } = createValidationErrorHandler(
    setSelectedTab,
    setSelectedLabel,
    tabLabels
  );

  const handleProfileSubmit = async (values, { setSubmitting }) => {
    try {
      const data = {
        patient_information: {
          ...omit(
            ["primary_phone_number", "secondary_phone_number"],
            values.patient_information
          ),
          phone_numbers: [
            path(["patient_information", "phone_number"], values),
            path(["patient_information", "mobile_number"], values)
          ]
        },
        physician_information: values.physician_information,
        physicians: values.physicians,
        patient_insurances: values.patient_insurances
      };
      const result = await dispatch(updatePatientProfile(data));
      if (result.success) {
        await dispatch(fetchPatientProfile());
        setEditMode(false);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      NotificationManager.error(
        translate("failedToUpdateProfile"),
        translate("error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsFetching(true);
        await dispatch(fetchPatientProfile());
      } finally {
        setIsFetching(false);
      }
    };
    loadData();
  }, [dispatch]);

  const handleChange = (_, newValue) => {
    setSelectedTab(newValue);
    setSelectedLabel(tabLabels[newValue]);
  };

  const {
    handleKeyDown,
    handleGroupFocus,
    focusFirstFormElement,
    focusEditButton
  } = useKeyboardNavigation({
    currentGroup,
    setCurrentGroup,
    selectedTab,
    setSelectedTab,
    setSelectedLabel,
    tabLabels,
    isSmallScreen,
    editMode,
    saveButtonRef,
    cancelButtonRef,
    editButtonRef
  });

  return (
    <Page.Container>
        <Page.Header title={translate("profile")} goBack={"/"} />
        <Page.Content isFetching={isFetching}>
          <Formik
            initialValues={getInitialValues(profile)}
            validationSchema={editProfileFormValidation}
            validateOnChange={true}
            validateOnBlur={true}
            validateOnMount={false}
            onSubmit={handleProfileSubmit}
            enableReinitialize={true}
          >
            {formProps => (
              <div className="profile-page-content" id="profile">
                <div className="page-content-header">
                  <div className="profile-actions">
                    <BreadCrumbs
                      childLabel="myInfo"
                      parentLink="/profile"
                      parentLinkLabel="profile"
                      title={selectedLabel}
                    />

                    {selectedTab !== 2 && !isSmallScreen && (
                      <ButtonActions
                        formProps={formProps}
                        editMode={editMode}
                        isSmallScreen={isSmallScreen}
                        translate={translate}
                        setEditMode={setEditMode}
                        currentGroup={currentGroup}
                        handleKeyDown={handleKeyDown}
                        showValidationErrors={showValidationErrors}
                        saveButtonRef={saveButtonRef}
                        cancelButtonRef={cancelButtonRef}
                        editButtonRef={editButtonRef}
                        focusFirstFormElement={focusFirstFormElement}
                        focusEditButton={focusEditButton}
                        selectedTab={selectedTab}
                      />
                    )}
                  </div>
                </div>
                <Tabs
                  value={selectedTab}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => handleGroupFocus("tabs")}
                  data-navigation-group="tabs"
                  aria-label="Profile navigation tabs"
                  indicatorColor="primary"
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  selectionFollowsFocus
                  className="profile-tabs-list"
                >
                  <Tab label={tabLabels[0]} id="profile-tab-0" aria-controls="profile-tabpanel-0" />
                  <Tab label={tabLabels[1]} id="profile-tab-1" aria-controls="profile-tabpanel-1" />
                  <Tab label={tabLabels[2]} id="profile-tab-2" aria-controls="profile-tabpanel-2" />
                </Tabs>
                <div className="profile-tabs-content">
                  {renderTabContent(selectedTab, editMode, formProps, profile)}
                </div>

                {selectedTab !== 2 && isSmallScreen && (
                  <div className="profile-actions">
                    <ButtonActions
                      formProps={formProps}
                      editMode={editMode}
                      isSmallScreen={isSmallScreen}
                      translate={translate}
                      setEditMode={setEditMode}
                      currentGroup={currentGroup}
                      handleKeyDown={handleKeyDown}
                      showValidationErrors={showValidationErrors}
                      saveButtonRef={saveButtonRef}
                      cancelButtonRef={cancelButtonRef}
                      editButtonRef={editButtonRef}
                      focusFirstFormElement={focusFirstFormElement}
                      focusEditButton={focusEditButton}
                      selectedTab={selectedTab}
                    />
                  </div>
                )}
              </div>
            )}
          </Formik>
        </Page.Content>
      </Page.Container>
  );
};

export default MyProfile;
